import { AUTH_JWT_TOKEN } from "@compass/core";
import * as signalR from "@microsoft/signalr";
import { HubName, TextingChatHubConnectParams } from "generated/openApi";
import { useEffect, useState, useRef } from "react";

export const useTextingChatHubConnection = (props: { conversationId: number | undefined }) => {
  const jwtToken = localStorage.getItem(AUTH_JWT_TOKEN);
  const [connectionStatus, setConnectionStatus] = useState<signalR.HubConnectionState>(
    signalR.HubConnectionState.Disconnected
  );
  const hubConnectionRef = useRef<signalR.HubConnection | null>(null);

  // Effect to handle connection/reconnection when conversationId changes
  useEffect(() => {
    // Stop any existing connection first
    const stopExistingConnection = async () => {
      if (hubConnectionRef.current && hubConnectionRef.current.state !== signalR.HubConnectionState.Disconnected) {
        try {
          await hubConnectionRef.current.stop();
          console.log("Previous connection stopped");
        } catch (err) {
          console.error("Error stopping previous connection:", err);
        }
      }
    };

    const setupNewConnection = async () => {
      // Only create a connection if we have a conversationId
      if (props.conversationId === undefined) {
        setConnectionStatus(signalR.HubConnectionState.Disconnected);
        return;
      }

      // Set up the connection with conversationId in the URL
      const url = `${process.env.REACT_APP_TEXTING_API_ENDPOINT}/${HubName[HubName.TextingChatHub]}?${
        TextingChatHubConnectParams[TextingChatHubConnectParams.ConversationId]
      }=${props.conversationId}`;

      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(url, {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
          accessTokenFactory: () => jwtToken!,
        })
        .withAutomaticReconnect()
        .build();

      // Set up connection state change handlers
      newConnection.onclose(() => {
        console.log("Connection closed");
        setConnectionStatus(signalR.HubConnectionState.Disconnected);
      });

      newConnection.onreconnecting(() => {
        console.log("Connection reconnecting");
        setConnectionStatus(signalR.HubConnectionState.Reconnecting);
      });

      newConnection.onreconnected(() => {
        console.log("Connection reconnected");
        setConnectionStatus(signalR.HubConnectionState.Connected);
      });

      // Start the connection
      try {
        setConnectionStatus(signalR.HubConnectionState.Connecting);
        await newConnection.start();
        console.log(`Connection established for conversation: ${props.conversationId}`);
        setConnectionStatus(signalR.HubConnectionState.Connected);

        // Store the new connection
        hubConnectionRef.current = newConnection;
      } catch (err) {
        console.error("Connection failed:", err);
        setConnectionStatus(signalR.HubConnectionState.Disconnected);
      }
    };

    const connect = async () => {
      await stopExistingConnection();
      await setupNewConnection();
    };

    connect();

    // Cleanup on unmount or conversationId change
    return () => {
      stopExistingConnection();
    };
  }, [props.conversationId, jwtToken]);

  return {
    textingChatHubConnection: hubConnectionRef.current,
    connectionStatus,
  };
};
